import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { dropdownOptionProps } from "../../@types/assetTypes/ap";
import ArrowDown from "../../assets/Icons/ArrowDown";
import SearchClose from "../../assets/Icons/SearchClose";
import { useAnchoredMenu } from "../../hooks/useAnchoredMenu";

interface IProps {
  disabled: boolean;
  options: dropdownOptionProps[];
  selected: Array<number | string>;
  onSelect: (value: number | string) => void;
  alterStyle?: string;
  fullWidth?: boolean;
}

const SearchableMultiSelectDropdown: React.FC<IProps> = ({
  disabled,
  options,
  selected,
  onSelect,
  alterStyle,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  const filteredOptions: dropdownOptionProps[] = options.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  // The menu is rendered in a portal on document.body so it is not clipped by
  // the parent dialog's overflow. It is positioned (fixed) against the trigger.
  const { menuRef, menuStyle } = useAnchoredMenu<HTMLDivElement>(
    dropdownRef,
    isOpen,
  );

  useEffect(() => {
    if (isOpen)
      menuRef.current?.querySelector("input")?.focus({ preventScroll: true });
  }, [isOpen]);

  useEffect(() => {
    try {
      const handleClickOutside = (event: MouseEvent): void => {
        const target = event.target as Node;

        const outsideTrigger =
          dropdownRef.current && !dropdownRef.current.contains(target);

        // the menu lives in a portal outside dropdownRef, so check it separately
        const outsideMenu = !menuRef.current?.contains(target);

        if (outsideTrigger && outsideMenu) {
          setIsOpen(false);
          setInputValue("");
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } catch (e) {
      console.error(e);
    }
  }, []);

  return (
    <div
      className={[
        fullWidth ? "w-full" : "w-96",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        "h-10 flex justify-between gap-3 border items-center shadow-custom shadow-blue-200 capitalize",
        alterStyle && alterStyle !== "" ? alterStyle : "",
      ].join(" ")}
      ref={dropdownRef}
      data-testid="form-drp"
    >
      <div className="relative w-full h-full">
        <div className="flex w-full h-full justify-between">
          <div
            className="flex w-full pl-5 py-1 items-center"
            onClick={disabled ? undefined : () => setIsOpen(true)}
          >
            {selected.length === 0 ? "" : `${selected.length} selected`}
          </div>
          {selected.length > 0 && (
            <div
              className="flex justify-center items-center py-1 px-2.5"
              onClick={
                disabled
                  ? undefined
                  : () => {
                      setIsOpen(true);
                      onSelect("__none__");
                      menuRef.current
                        ?.querySelector("input")
                        ?.focus({ preventScroll: true });
                    }
              }
            >
              <SearchClose className="w-4 h-4" />
            </div>
          )}
          <div
            className="flex justify-center items-center py-1 px-2.5 pr-5"
            onClick={disabled ? undefined : () => setIsOpen(true)}
          >
            <ArrowDown
              className={`transition-all duration-500 ${
                isOpen ? "-rotate-180" : ""
              }`}
            />
          </div>
        </div>
        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="shadow-custom shadow-blue-200 rounded-md overflow-hidden bg-white z-[60] flex flex-col"
              // portalled menu still bubbles React events to the dialog; stop
              // mousedown so the dialog's "click outside to close" does not fire
              onMouseDown={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                data-testid="inputparent"
                className={"pl-5 py-1 w-full outline-none border-b"}
                placeholder="Search"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoComplete="off"
              />
              <ul className="w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                {filteredOptions.length === 0 && (
                  <li className="capitalize px-5 py-2 text-gray-500">
                    Not Found
                  </li>
                )}
                {filteredOptions.map((option) => (
                  <li
                    className="cursor-pointer capitalize px-5 py-1 hover:bg-[#211551] hover:text-white group"
                    key={option.value}
                    onClick={() => {
                      onSelect(option.value);
                      menuRef.current
                        ?.querySelector("input")
                        ?.focus({ preventScroll: true });
                    }}
                  >
                    <div className="flex w-full space-x-2">
                      <input
                        checked={selected.includes(option.value)}
                        type="checkbox"
                        readOnly
                        className="w-4 h-4 accent-[#211551] group-hover:accent-white mt-1 shrink-0"
                      />
                      <span className="min-w-0 break-words">
                        {option.label}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default SearchableMultiSelectDropdown;
